import { __awaiter } from "tslib";
import { useAemConfig } from '@as-react/common-components';
import { checkoutMutations } from '@as-react/common-queries';
import { ErrorMessage, PaymentGroupName, PaymentProvider, RoutePathname, } from '@as-react/common-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { generatePathUrl } from '../util/URLParamUtil';
import { analyticsActions } from '../redux/analytics/actions/AnalyticsActions.creators';
import Klarna from '../util/KlarnaUtil';
import BasketUtil from '../util/BasketUtil';
import { usePaymentState } from '../context/CheckoutPaymentContext';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
export const useSavePaymentOption = () => {
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const { defaultRequestParams, servicesEndpoint, root, isInAuthorMode } = useAemConfig();
    const dispatch = useDispatch();
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [{ info }] = usePaymentState();
    const { postOrderPaidByVoucher, postPayment, postPaymentProviderInput, postSubmitOrder } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutateAsync: orderPaidByVoucher } = useMutation(postOrderPaidByVoucher);
    const { mutateAsync: payment } = useMutation(postPayment);
    const { mutateAsync: paymentProviderInput } = useMutation(postPaymentProviderInput);
    const { mutate: submitOrder } = useMutation(postSubmitOrder);
    const savePaymentOptions = (...args_1) => __awaiter(void 0, [...args_1], void 0, function* ({ group } = {}) {
        try {
            setIsLoading(true);
            if (info.fullyPaid) {
                const { orderReference } = yield orderPaidByVoucher();
                const search = { status: 'accept', orderID: orderReference };
                const location = generatePathUrl(root, `buy/${RoutePathname.CONFIRMATION}.html`, isInAuthorMode, search);
                window.location.href = `${location.pathname}?${location.search}`;
            }
            else if (group) {
                const options = group.paymentOptions;
                const isSingleOption = options.length === 1;
                let optionId;
                let provider;
                let optionName;
                if (isSingleOption || group.name === PaymentGroupName.ECOCHEQUE) {
                    optionId = options[0].paymentOptionId;
                    provider = options[0].provider;
                    optionName = options[0].name;
                }
                else {
                    const findOption = options.find(opt => opt.name === group.name);
                    optionId = findOption.paymentOptionId;
                    provider = findOption.provider;
                    optionName = findOption.name;
                }
                const providerInformation = yield payment({
                    chosenPaymentOption: optionName,
                    chosenPaymentOptionId: optionId,
                    provider,
                    tp: `${defaultRequestParams.mainWebshop}.html`,
                });
                dispatch(analyticsActions.paymentRequest({ paymentGroup: group.name, paymentMethod: optionName }));
                if (providerInformation.provider === PaymentProvider.SAFERPAY ||
                    providerInformation.provider === PaymentProvider.WORLDLINE) {
                    const { redirectUrl } = yield paymentProviderInput(providerInformation.provider);
                    if (redirectUrl) {
                        location.href = redirectUrl;
                    }
                }
                else if (providerInformation.provider === PaymentProvider.KLARNA) {
                    const orderReference = info.orderReference;
                    Klarna.authorize([providerInformation.paymentMethodCategory], orderReference, result => {
                        if (result.approved === true) {
                            submitOrder({ provider: providerInformation.provider, authorizationToken: result.authorization_token }, {
                                onSuccess: redirectUrl => {
                                    location.href = redirectUrl;
                                },
                                onError: () => {
                                    setError(t(ErrorMessage.GENERAL_PAYMENT));
                                    setIsLoading(false);
                                },
                            });
                        }
                        else {
                            setIsLoading(false);
                        }
                    });
                }
            }
            setError(undefined);
            return true;
        }
        catch (error) {
            if (BasketUtil.isBasketNotInitialized(error)) {
                location.href = `${root}checkout/basket-overview.html`;
                return;
            }
            setError(t(ErrorMessage.GENERAL_PAYMENT));
            setIsLoading(false);
            return false;
        }
    });
    return { savePaymentOptions, errorMessage: error, isLoading };
};
